import {
  always, mapObjIndexed, when, omit,
} from 'ramda';
import gql from 'graphql-tag';
import { mutate, query } from '@/api/graphql';
import isNullOrEmpty from '@/helpers/misc/isNullOrEmpty';
import ActiveUser from '@/api/user/graphql/fragments/activeUser.graphql';
import GetAgencyContents from './graphql/queries/GetAgencyContents.graphql';
import GetNewAgencyContentsCount from './graphql/queries/GetNewAgencyContentsCount.graphql';
import AgencyContents from './graphql/fragments/agencyContents.graphql';
import GetAgencies from './graphql/queries/GetAgencies.graphql';
import Agencies from './graphql/fragments/agencies.graphql';
import GetAgencyCategories from './graphql/queries/GetAgencyCategories.graphql';
import GetActiveUsersOfAgencyContents from './graphql/queries/GetActiveUsersOfAgencyContents.graphql';
import AgencyCategories from './graphql/fragments/agencyCategories.graphql';
import UpdateStatusToAgencyContent from './graphql/mutations/UpdateStatusToAgencyContent.graphql';
import UpdateStatusToAgencyArchive from './graphql/mutations/UpdateStatusToAgencyArchive.graphql';
import GetAgencyContentById from './graphql/queries/GetAgencyContentById.graphql';
import SaveAgencyContent from './graphql/mutations/SaveAgencyContent.graphql';
import DeleteAgencyArchiveContent from './graphql/mutations/DeleteAgencyArchiveContent.graphql';

export const getAgencyContents = async ({
  filter, page, limit, firstCreatedDate,
}) => {
  const modifiedFilter = mapObjIndexed(when(isNullOrEmpty, always(undefined)), filter);

  const variables = {
    filter: omit(['isApproved'], modifiedFilter),
    page,
    limit,
    firstCreatedDate,
  };

  const { agencyContents } = await query(gql`
    ${GetAgencyContents}
    ${AgencyContents}
  `, variables);

  return agencyContents;
};

export const getActiveUsersOfAgencyContents = async () => {
  const { activeUsersOfAgencyContents } = await query(gql`
    ${GetActiveUsersOfAgencyContents}
    ${ActiveUser}
  `);

  return activeUsersOfAgencyContents;
};

export const getAgencies = async (contentTypes, tabName) => {
  const variables = {
    contentTypes,
    page: 0,
    limit: 0,
    tabName,
  };

  const { agencies } = await query(gql`
    ${GetAgencies}
    ${Agencies}
  `, variables);

  return agencies;
};

export const getAgencyCategories = async filter => {
  const { agencyCategories } = await query(gql`
    ${GetAgencyCategories}
    ${AgencyCategories}
  `, { filter });

  return agencyCategories;
};

export const saveAgencyContent = async input => {
  const variables = {
    input,
  };

  const result = await mutate(gql`
      ${SaveAgencyContent}
      ${AgencyContents}
    `,
  variables);

  return result;
};

export const updateStatusToAgencyContent = async ({
  agencyContentId,
  status,
}) => {
  const variables = { agencyContentId, status };

  const result = await mutate(gql`
    ${UpdateStatusToAgencyContent}
  `, variables);

  return result || false;
};

export const updateStatusToAgencyArchive = async ({
  agencyArchiveId,
  status,
}) => {
  const variables = { agencyArchiveId, status };

  const result = await mutate(gql`
    ${UpdateStatusToAgencyArchive}
  `, variables);

  return result || false;
};

export const deleteAgencyArchiveContent = async id => {
  const result = await mutate(gql`
    ${DeleteAgencyArchiveContent}
  `, { id });

  return !!result?.deleteAgencyArchiveContent;
};

export const getNewAgencyContentsCount = async ({
  filter, afterId,
}) => {
  const modifiedFilter = mapObjIndexed(when(isNullOrEmpty, always(undefined)), filter);

  const variables = {
    filter: omit(['isApproved'], modifiedFilter),
    afterId,
  };

  const { newAgencyContentsCount } = await query(gql`
    ${GetNewAgencyContentsCount}
  `, variables);

  return newAgencyContentsCount || 0;
};

export const getAgencyContentById = async id => {
  const { agencyContent } = await query(gql`
    ${GetAgencyContentById}
  `, { id });

  return agencyContent;
};
